@if (loadingBrand) {
    <app-wait></app-wait>
}

@if (fromDocusign) {
    <div class="docusign-success-message">
        <app-message-box level="success">Thank you for signing your document. You can optionally sign into your Sircon
            account to view all of your documents, manage your carrier relationships, and get access to various
            compliance related services.
        </app-message-box>
    </div>
}

<div class="login-view">
    @if (!loadingBrand) {
        <div class="login-left-side">
            <div [ngClass]="[
                brandKey ? 'branded login' : 'login',
                (mfaChallengeState || mfaEnrollmentRequired) ? 'mfa-challenge-mode' : ''
            ]">
                <div class="login-panel-header">
                    <div class="login-header-title">
                        @if (brandKey) {
                            <img src="/images/sircon-logo-white.svg" alt="Sircon, a Vertafore company"> |
                        }
                        @if (mfaChallengeState) {
                            Two-Step Verification
                        } @else if (mfaEnrollmentRequired) {
                            Set Up Two-Step Verification
                        } @else {
                            Sign In
                        }
                    </div>
                </div>

                <div class="login-info">
                    @if (mfaChallengeState) {
                        <app-mfa-challenge
                            [challengeInput]="{
                                challengeName: mfaChallengeState.challengeName,
                                session: mfaChallengeState.session,
                                userName: mfaChallengeState.userName
                            }"
                            (verified)="completeMfaChallenge($event)">
                        </app-mfa-challenge>
                    } @else if (mfaEnrollmentRequired) {
                        <app-mfa-enrollment-required
                            (verified)="completeMfaEnrollment()">
                        </app-mfa-enrollment-required>
                    } @else {
                        <form (ngSubmit)="authenticate()">
                        @if (confirmingSso) {
                        <app-message-box level="info"
                                         prominent="Verify your Sircon account"
                                         [messages]="{message: 'Please sign in to verify your sircon account.'}"></app-message-box>
                    }

                    @if (invitationAccepted) {
                        <app-message-box level="info"
                                         prominent="Connection request already accepted"
                                         [messages]="{message: 'Please sign in below.'}"></app-message-box>
                    }

                    @if (invitationWrongUserType) {
                        <app-message-box level="error"
                                         prominent="This invitation is not compatible with your existing account">
                            Please <a routerLink="/inviteSignup/landing"><strong>Create a New Account</strong></a> using a
                            different email, or
                            <a routerLink="/inviteSignup/clearInvite"><strong>Continue to Your Existing Account</strong></a>
                            @if (invitationType === 'agency_edge_connection') {
                                <span>(you will not be connected to this agency)</span>
                            }
                            @if (invitationType === 'carrier_request') {
                                <span>(you will not be able to complete your carrier onboarding)</span>
                            }
                        </app-message-box>
                    }

                    @if (invitationNoLongerValid) {
                        <app-message-box level="error"
                                         prominent="This invitation is no longer valid."
                                         [messages]="{message: 'Please sign in below.'}"></app-message-box>
                    }

                    @if (inviteTokenConsumed) {
                        <app-message-box level="error"
                                         prominent="This invitation is no longer valid.">
                            You've already accepted this invitation or it's no longer valid. Please contact your
                            organization if you
                            have questions. If you have an account, you may login.
                        </app-message-box>
                    }

                    @if (invalidRecoveryEmailToken) {
                        <app-message-box level="error"
                                         prominent="This link is no longer valid.">
                            You've already used this link or it expired.
                            You may check the status of your recovery email in Account Settings.
                        </app-message-box>
                    }

                    @if (unauthenticatedError) {
                        <app-message-box level="error"
                                         prominent="Your session has expired"
                                         [messages]="{message: 'Please sign in below.'}"></app-message-box>
                    }

                    @if (passwordUpdated) {
                        <app-message-box level="success"
                                         prominent="Your password has been updated. "
                                         [messages]="{message: 'Please sign in below'}"></app-message-box>
                    }

                    @if (errors.validationError) {
                        <app-message-box level="error" prominent="Your email or password was incorrect">
                            Please note: Four consecutive failed login attempts will lock your account for 15 minutes.
                            Did you know most login issues can be remedied by resetting your password?
                            <a routerLink="/forgotPassword">Reset Your Password</a> now.
                        </app-message-box>
                    }

                    @if (successMessage) {
                        <app-message-box level="success"
                                         prominent="Success"
                                         [messages]="{message: successMessage}"></app-message-box>
                    }

                    @if (errors.unexpectedError) {
                        <app-message-box serviceUnavailable></app-message-box>
                    }

                    <app-input fieldId="username" label="Email" [(ngModel)]="user.userName"
                               name="username"
                               (change)="reinitializeUserNameIfFrozen()" type="email" inputmode="email"
                               [isDisabled]="freezeUsername()"
                               autoFocus [errors]="errors.fieldErrors.userName" isRequired></app-input>

                    <app-input fieldId="password" label="Password" type="password" [(ngModel)]="user.password"
                               [attr.autoFocus]="freezeUsername()"
                               name="password"
                               [errors]="errors.fieldErrors.password" isRequired></app-input>

                    <div class="suif-h6 forgot-password-link">
                        <a routerLink="/login/help">Forgot password or lost access to your email? Get help signing in</a>
                    </div>

                    <div class="button-row">
                        @if (cameFromInvite) {
                            <button type="button" class="btn btn-secondary" (click)="goBack()">Back</button>
                        }
                        <app-wait-button cssClass="btn btn-verty" [waiting]="submitting"
                                         label="Sign In"></app-wait-button>
                    </div>
                        </form>
                    }
                </div>
            </div>

            @if (!cameFromInvite && !carrierInformation && !mfaChallengeState && !mfaEnrollmentRequired) {
                <div class="create-account suif-body-copy">
                    Don't Have an Account? <a routerLink="/inviteSignup/initiateInvite">Sign Up</a>
                </div>
            }

            <div class="walkup-help">
                @if (carrierInformation) {
                    <a target="_blank" href="{{brandedWalkupHelpUrl()}}">Sircon Help & Support</a>
                }
            </div>
        </div>
    }

    @if (brandKey) {
        <div class="brand-panel">
            <app-brand-information (loadingBrandEvent)="loadingBrandEvent($event)"
                                   (loadingErrorEvent)="loadingBrandErrorEvent()"
                                   (carrierInformationEvent)="carrierInformationEvent($event)"
                                   [messageOverride]="confirmingSso ? 'Please login to your Sircon account to confirm you own the account.' : null">
            </app-brand-information>
        </div>
    }
</div>
